import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useIdentity } from '@/entities/session';
import type { Rule } from '@/shared/api/generated/rules';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { TextField } from '@/shared/ui/Field';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import {
  useDecideTransition,
  useRequestTransition,
  useTransitionRequest,
} from '../api/rules';

/* The two-eyes approval, as a thing somebody can actually do.
 *
 * **This is the screen the platform has been telling people to use.** Activating a rule refuses with
 * *"activate requires a satisfied two-eyes approval and none exists. Submit a transition request,
 * collect two signatures from people other than the requester, then retry."* — and until now the app
 * had no way to submit a request, no way to sign one, and no way to see whether the first signature
 * had arrived. Three endpoints existed; nothing in the client called any of them.
 *
 * **Who approves, and why an admin is not exempt.** The signature needs `rules.rule:activate`, which
 * the `approver` and `admin` roles hold and `data-steward` does not — the catalogue keeps a steward
 * from activating what they authored, and keeps an author from signing their own work whatever role
 * they hold. `ApprovalService` refuses the requester's own signature by identity, not by permission,
 * so **an admin cannot approve a rule they requested** and can approve anybody else's. That is the
 * whole mechanism: two-eyes is about two *people*, not about seniority, and an exemption for the most
 * privileged account is precisely the hole it exists to close.
 *
 * **The requester's own signature is refused server-side and hidden here, and that is a change from
 * this component's first version.** It used to show Reject/Approve to the requester too, on the
 * argument that a client cannot know identities well enough to gate on them and that hiding the
 * control leaves an author guessing whether they lack a permission or are the requester. `useIdentity`
 * closes exactly that gap — `current.requester` is the token `sub` the server itself compares
 * signatures against, and `identity.data.subject` is the same string for the caller, so the comparison
 * is the same one the server makes. What replaces the buttons is a sentence naming the situation
 * directly, which is less ambiguous than a control that would 403 on the first click. The refusal
 * remains real and server-enforced regardless of what this component shows — this is a presentation
 * choice sitting on top of a security property it does not itself provide.
 *
 * **Every signature carries a reason, and the field is required.** The server refuses an empty one —
 * "a transition decision states its reason" — because the reason is what an auditor reads a year
 * later, and "approved" with no sentence is a signature nobody can account for.
 */

export interface TransitionApprovalProps {
  readonly rule: Rule;
}

export function TransitionApproval({
  rule,
}: TransitionApprovalProps): ReactElement | null {
  const { t } = useTranslation();
  const identity = useIdentity();
  const open = useTransitionRequest(rule.id);
  const request = useRequestTransition();
  const decide = useDecideTransition();
  const [reason, setReason] = useState('');

  /* Only a draft has an activation to govern. An active rule's approval is spent — migration 0042
   * made it single-use, so a satisfied request cannot be reused for a later transition — and a
   * deprecated rule is not reactivated at all. */
  if (rule.state !== 'draft') return null;

  if (open.isPending) {
    return (
      <p role="status" className="lq-muted">
        {t('approval.loading')}
      </p>
    );
  }

  if (open.isError) {
    /* A failed read is about the request, and it must not read as "no approval exists" — that would
     * invite an author to open a second request the kernel would refuse. */
    return (
      <Alert tone="warning" title={t('approval.unreadableTitle')}>
        {open.error.message}
      </Alert>
    );
  }

  const current = open.data;

  return (
    <Card title={t('approval.title')} hint={t('approval.hint')}>
      {current === null ? (
        <>
          <p className="lq-muted">{t('approval.noneOpen')}</p>
          {request.isError && <Alert tone="error">{request.error.message}</Alert>}
          <div style={{ marginTop: 'var(--space-3)' }}>
            <Button
              tone="primary"
              busy={request.isPending}
              onClick={() => {
                request.mutate(rule.id);
              }}
            >
              {t('approval.request')}
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="lq-toolbar">
            <StatusBadge
              tone={current.satisfied ? 'ok' : 'warn'}
              label={
                current.satisfied
                  ? t('approval.state.satisfied')
                  : t('approval.state.waiting')
              }
              title={
                current.satisfied
                  ? t('approval.state.satisfiedMeaning')
                  : t('approval.state.waitingMeaning')
              }
            />
            {/* The count, and it is the figure the refusal message is about. `eyes_collected` of
                `required_eyes` rather than a bar: two is not a progress bar's worth of steps, and the
                number is what an approver checks before signing. */}
            <span className="lq-muted">
              {t('approval.eyes', {
                collected: current.eyes_collected,
                required: current.required_eyes,
              })}
            </span>
          </div>

          <p className="lq-muted">
            {t('approval.requestedBy', {
              who: current.requester,
              at: new Date(current.submitted_at),
            })}
          </p>

          {current.decisions.length > 0 && (
            <ul className="lq-signatures">
              {current.decisions.map((decision) => (
                <li
                  key={`${decision.approver}-${decision.decided_at}`}
                  className="lq-signatures__row"
                >
                  <StatusBadge
                    tone={decision.kind === 'approve' ? 'ok' : 'danger'}
                    label={t(`approval.decision.${decision.kind}`)}
                  />
                  <span className="lq-signatures__who">{decision.approver}</span>
                  {/* The reason, verbatim. It is the thing an auditor reads a year later, and
                      summarising it here would make this screen the place the summary drifts. */}
                  <span className="lq-signatures__reason">{decision.reason}</span>
                </li>
              ))}
            </ul>
          )}

          {current.satisfied ? (
            /* Satisfied, and the rule is still a draft — so the remaining step is the activation
               itself, which is the button in the page header. Said here because an approver who has
               just signed the second signature has no other way to know they are done. */
            <Alert tone="notice" title={t('approval.readyTitle')}>
              {t('approval.readyBody')}
            </Alert>
          ) : identity.data?.subject === current.requester ? (
            /* **This caller is the requester, and the comparison is the same one the server makes.**
               `current.requester` is the token `sub` `ApprovalService` refuses a signature from;
               `identity.data.subject` is the same claim for whoever is looking at this screen. A
               click here would always 403, so the sentence replaces the click rather than waiting to
               explain it after. */
            <Alert tone="notice" title={t('approval.ownRequestTitle')}>
              {t('approval.ownRequestBody')}
            </Alert>
          ) : (
            <>
              {decide.isError && (
                <Alert tone="warning" title={t('approval.refusedTitle')}>
                  {decide.error.message}
                </Alert>
              )}
              <div className="lq-grid">
                <TextField
                  label={t('approval.reasonLabel')}
                  value={reason}
                  onChange={setReason}
                  required
                  maxLength={500}
                  hint={t('approval.reasonHint')}
                />
              </div>
              <div className="lq-toolbar" style={{ marginTop: 'var(--space-3)' }}>
                {/* Reject first in the DOM, approve second — the `ConfirmDialog` ordering rule, for
                    the same reason: the consequential control should not be the one under a cursor
                    that arrived by habit. Both are disabled without a reason, because the server
                    refuses an empty one and a round trip to learn that is a wasted one. */}
                <Button
                  tone="secondary"
                  busy={decide.isPending}
                  disabled={reason.trim() === ''}
                  onClick={() => {
                    decide.mutate({ ruleId: rule.id, approved: false, reason });
                  }}
                >
                  {t('approval.reject')}
                </Button>
                <Button
                  tone="primary"
                  busy={decide.isPending}
                  disabled={reason.trim() === ''}
                  title={t('approval.approveMeaning')}
                  onClick={() => {
                    decide.mutate({ ruleId: rule.id, approved: true, reason });
                  }}
                >
                  {t('approval.approve')}
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </Card>
  );
}
